import { FC } from "react";
import UserGroupPageMappingType, { CheckedMenuList, FlatMenu, Page, PageGroup } from "./Types/MenuAndSubmenuType";
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import { makeStyles } from '@material-ui/core/styles';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, IconButton, Tooltip, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Info from "@material-ui/icons/Info";
import CustomSkeleton from "src/components/skeleton/Skeleton";

type MenuTreeForWidgetsProps = {
  data: UserGroupPageMappingType[];
  checkedList: CheckedMenuList[];
  setCheckedList: any;
  isTreeLoading: boolean;
  flatMenuList: FlatMenu[]
}

type MenuTreeWithUserGroupsProps = {
  data: UserGroupPageMappingType;
  checkedList: CheckedMenuList[];
  setCheckedList: any;
  isTreeLoading: boolean;
  menuList: FlatMenu[];
};

type MenuItemProps = {
  userGroupKey: string;
  item: PageGroup | Page;
  checkedList: CheckedMenuList[];
  setCheckedList: any;
  menuList: FlatMenu[];
};

const useStyles = makeStyles({
  root: {
    height: 'auto',
    flexGrow: 1,
    width: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  treeView: {
    flexGrow: 1,
    width: '100%'
  },
  tree: {
    marginBottom: '8%'
  }
});

const MenuItem: FC<MenuItemProps> = ({ userGroupKey, item, checkedList, setCheckedList, menuList }) => {
  function isPageGroup(item: PageGroup | Page): item is PageGroup {
    return (item as PageGroup).pageGroupName !== undefined;
  }
  
  const shouldCheck = () => {
    if (isPageGroup(item)) {
      // If item is a PageGroup and has pages
      if (item.pages && item.pages.length > 0) {
        // Check if all pages in the PageGroup are checked
        return item.pages.every(y =>
          checkedList?.some(x =>
            x.UserGroupKey === userGroupKey &&
            x.PageGroupKey === item.pageGroupKey &&
            x.PageKey === y.pageKey
          )
        );
      } else {
        // If PageGroup has no pages, check the PageGroup itself
        return checkedList?.some(x =>
          x.UserGroupKey === userGroupKey &&
          x.PageGroupKey === item.pageGroupKey
        );
      }
    } else {
      // If item is a Page, check if the Page is checked
      if (checkedList?.some(x => x.UserGroupKey === userGroupKey && x.PageGroupKey === item.pageGroupKey && x.PageKey === item.pageKey)) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isIndeterminate = () => {
    if (isPageGroup(item)) {
      if (!item.pages || item.pages.length === 0) {
        return false;
      } else {
        let checkedCount = 0;
        item.pages.forEach((page) => {
          if (checkedList?.some(x => x.UserGroupKey === userGroupKey && x.PageGroupKey === item.pageGroupKey && x.PageKey === page.pageKey)) {
            checkedCount++;
          }
        })
        return checkedCount > 0 && checkedCount < item.pages.length;
      }
    } else {
      return false;
    }
  };
  
  // Old Implementation
  // const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   event.stopPropagation();
  //   if (event.target.checked) {
  //     if (isPageGroup(item)) {
  //       if (item.pages && item.pages.length > 0) {
  //         const listOfChildren = [...item.pages.map((x) => ({ UserGroupKey: userGroupKey, PageGroupKey: item.pageGroupKey, PageKey: x.pageKey }))];
  //         setCheckedList((prev) => [...prev, ...listOfChildren]);
  //       } else {
  //         setCheckedList((prev) => [...prev, { UserGroupKey: userGroupKey, PageGroupKey: item.pageGroupKey, PageKey: null }]);
  //       }
  //     } else {
  //       setCheckedList((prev) => [...prev, { UserGroupKey: userGroupKey, PageGroupKey: item.pageGroupKey, PageKey: item.pageKey }]);
  //     }
  //   } else {
  //     if (isPageGroup(item)) {
  //       const filteredList = checkedList.filter((x) => !(x.UserGroupKey === userGroupKey && x.PageGroupKey === item.pageGroupKey));
  //       setCheckedList((prev) => filteredList);
  //     } else {
  //       const filteredList = checkedList.filter((x) => !(x.UserGroupKey === userGroupKey && x.PageGroupKey === item.pageGroupKey && x.PageKey === item.pageKey));
  //       setCheckedList((prev) => filteredList);
  //     }
  //   }    
  // };
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.target.checked) {
      if (isPageGroup(item)) {
        if (!item.pages || item.pages.length === 0) {
          const updatedCheckedList = menuList.filter(x => x.PageGroupKey === item.pageGroupKey && x.IsEnabled).map(x => ({
            PageGroupKey: item.pageGroupKey,
            PageKey: null,
            UserGroupKey: x.UserGroupKey
          }))
          setCheckedList(updatedCheckedList);
        }
      } else {
        const updatedCheckedList = menuList.filter(x => x.PageGroupKey === item.pageGroupKey && item.pageKey === x.PageKey && x.IsEnabled).map(x => ({
          PageGroupKey: item.pageGroupKey,
          PageKey: item.pageKey,
          UserGroupKey: x.UserGroupKey
        }));
        setCheckedList(updatedCheckedList);
      }
    } else {
      setCheckedList([]);
    }    
  };


  if (isPageGroup(item)) {
    return (
      <TreeItem
        nodeId={`${userGroupKey}-${item.pageGroupKey}`}
        key={item.pageGroupKey}
        label={
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shouldCheck()}
                  indeterminate={isIndeterminate()}
                  onChange={handleCheckboxChange}
                  disabled={!item.isEnabled}
                />
              }
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', textOverflow: 'ellipsis' }}>
                  {item.pageGroupName}
                  {item.pageGroupAddress && item.pages.length === 0 && (
                    <IconButton sx={{ margin: '0', marginLeft: '4px', padding: '0' }}>
                      <Tooltip title={item.pageGroupAddress} placement="top">
                        <Info fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  )}
                </span>
              }
            />
          </>
        }
      >
        {item?.pages?.map((x, index) => (
          <MenuItem
            key={index}
            userGroupKey={userGroupKey}
            item={x}
            checkedList={checkedList}
            setCheckedList={setCheckedList}
            menuList={menuList}
          />
        ))}
      </TreeItem>
    );
  } else {
    return (
      <TreeItem
        nodeId={`${userGroupKey}-${item.pageKey}`}
        key={item.pageKey}
        label={
          <FormControlLabel
            control={
              <Checkbox
                checked={shouldCheck()}
                indeterminate={isIndeterminate()}
                onChange={handleCheckboxChange}
                disabled={!item.isEnabled}
              />
            }
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {item.pageName}
                {item.pageAddress && (
                  <IconButton style={{ margin: '0', padding: '0', marginLeft: '4px' }}>
                    <Tooltip title={item.pageAddress} placement="top">
                      <Info fontSize="small" />
                    </Tooltip>
                  </IconButton>
                )}
              </span>
            }
          />
        }
      />
    );
  }
};

const MenuTreeWithUserGroups: FC<MenuTreeWithUserGroupsProps> = ({ data, checkedList, setCheckedList, isTreeLoading, menuList }) => {
  const classes = useStyles();

  return (
    <div className={classes.tree}>
      {isTreeLoading && <CustomSkeleton skeletonType='TreeViewSkeleton' repeatSkeleton={5}/>}
      {!isTreeLoading && (
        <>
          <Accordion
            key={data.userGroupKey}
            sx={{ width: "98%" }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="tree-content"
              id="tree-header"
              sx={{ alignItems: "center" }}
            >
              <Tooltip title={data.userGroupName} placement="top">
                <Typography
                  variant="h5"
                  fontWeight='400'
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100% - 32px)'
                  }}
                >
                  {data.userGroupName}
                </Typography>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
              <TreeView
                className={classes.treeView}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
              >
                {data.pageGroups.map((item, index) => (
                  <MenuItem
                    item={item}
                    key={index}
                    checkedList={checkedList}
                    setCheckedList={setCheckedList}
                    userGroupKey={data.userGroupKey}
                    menuList={menuList}
                  />
                ))}
              </TreeView>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </div>
  );
};

const MenuTreeForWidgets: FC<MenuTreeForWidgetsProps> = ({ data, checkedList, setCheckedList, isTreeLoading, flatMenuList }) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      {data.map((item) => {
        return (
          <MenuTreeWithUserGroups
            key={item.userGroupKey}
            data={item}
            checkedList={checkedList}
            setCheckedList={setCheckedList}
            isTreeLoading={isTreeLoading}
            menuList={flatMenuList}
          />
        );
      })}
    </div>
  )
};

export default MenuTreeForWidgets;
